/**
 * ФОРМАТ МИССИИ СЮЖЕТНОЙ КАМПАНИИ
 *
 * Миссия — это JSON, который собирается «Редактором миссий» (админский режим)
 * и кладётся в код проекта. Она проигрывается тем же движком, что и обычная
 * локальная партия: поле и игроки восстанавливаются из конфига, а ходы
 * противников воспроизводятся записанным потоком redux-экшенов (см.
 * MissionRunner) — ровно так же, как это делает просмотр записи матча.
 *
 * {
 *   id: 'humans_1',                     // уникальный id миссии
 *   campaignFactionId: 'humans',        // кампания какой фракции
 *   order: 1,                           // порядок внутри кампании
 *   title, shortTitle, description,
 *   finalRound: 12 | null,              // null — раундов не ограничиваем
 *   reward: {type: 'faction'|'chest'|'premium', ...},
 *   rewardIfFactionOwned: {...},        // запасная награда (см. getTutorialChapterReward)
 *   playerColor: 'blue',                // за кого играет живой игрок
 *   players: [{color, name, factionId, selectedSkin, isBot, bonus}],
 *   field: {                            // стартовая расстановка (снимок редактора)
 *     hexes: {'0,0': 'central_quarter_blue', ...},
 *     units: [{id, hexKey, ownerId, type, size, maxSize}],
 *     specialBuildings: {...},
 *     gridConfig: {radius, hexSize, orientation},
 *   },
 *   policyMarket: [...],                // опционально; иначе рынок соберётся сам
 *   script: [ ...записи ленты... ],     // см. MISSION_SCRIPT_KINDS
 *   objectives: [ ...задания... ],      // см. missionObjectives.js
 * }
 *
 * ЛЕНТА (script) — линейная последовательность того, что редактор записал.
 * Записи проигрываются по порядку, каждая — в своём игровом моменте.
 */

// Виды записей ленты миссии
export const MISSION_SCRIPT_KINDS = {
  // Действие игрока-скрипта (противника): обычный redux-экшен, записанный
  // редактором. Воспроизводится, когда ход дошёл до этого игрока
  ACTION: 'action',
  // Реплика персонажа: картинка + текст, кнопка «Далее» в футере
  REPLICA: 'replica',
  // Появление задания (главного или промежуточного)
  OBJECTIVE: 'objective',
  // С этого момента за игрока ходит обычная система ботов, а не запись
  BOT_AUTO: 'bot_auto',
  // Появление стороны посреди миссии: добавляет игрока (если его ещё нет)
  // и/или спавнит его отряды в заданных гексах — «гномы выкапываются»
  SPAWN: 'spawn',
  // Сюжетный уход стороны со сцены: все отряды цвета снимаются с поля
  // (зверолюди уходят с моста, когорты складывают оружие)
  DESPAWN: 'despawn',
};

// С какой стороны экрана показывать персонажа реплики
export const REPLICA_SIDES = {
  LEFT: 'left',
  RIGHT: 'right',
};

export const REPLICA_SIDE_TITLES = {
  [REPLICA_SIDES.LEFT]: 'Слева',
  [REPLICA_SIDES.RIGHT]: 'Справа',
};

/**
 * Момент игры, в который записана запись ленты. Реплики и задания
 * привязываются именно к моменту, а не к порядковому номеру действия: ходы
 * живого игрока при прохождении будут отличаться от записанных в редакторе,
 * и жёсткая привязка к индексу разъехалась бы.
 */
export const createMissionMoment = (state) => {
  const gameState = state.shared.gameState;
  const currentPlayer = gameState.players[gameState.currentPlayerIndex];

  return {
    round: gameState.round,
    phase: gameState.phase,
    stage: gameState.warPhaseStage || null,
    playerColor: currentPlayer?.color || null,
  };
};

// Совпал ли текущий момент партии с моментом записи
export const checkIfMomentReached = (moment, currentMoment) => {
  if (!moment) return true;

  return moment.round === currentMoment.round
    && moment.phase === currentMoment.phase
    && (moment.stage || null) === (currentMoment.stage || null)
    && (!moment.playerColor || moment.playerColor === currentMoment.playerColor);
};

// Префикс токена «отряд игрока» в тулбаре редактора: mission_unit:<цвет>:<тип>
export const MISSION_UNIT_TOKEN_PREFIX = 'mission_unit:';

export const buildMissionUnitToken = (color, unitType) => (
  MISSION_UNIT_TOKEN_PREFIX + color + ':' + unitType
);

export const createEmptyMission = () => ({
  id: '',
  campaignFactionId: null,
  order: 1,
  title: '',
  shortTitle: '',
  description: '',
  finalRound: null,
  reward: null,
  rewardIfFactionOwned: null,
  playerColor: null,
  players: [],
  field: null,
  policyMarket: null,
  script: [],
  objectives: [],
});
